"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  amountText,
  GrowthChart,
  percentText,
  RulesTable,
  ScheduleTable,
  WorkingSteps,
} from "@/components/tools/algebra/growth-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type GrowthDirection,
  type GrowthInput,
  type GrowthModel,
  type GrowthUnknown,
  solveGrowth,
} from "@/lib/tool-kit/algebra/growth";

/** English strings. A translated content file overrides these through `ui`. */
export const EXPONENTIAL_GROWTH_CALCULATOR_STRINGS = {
  model: "Model",
  models: {
    discrete: "Discrete: A = P(1 + r)^t",
    continuous: "Continuous: A = Pe^(rt)",
  },
  direction: "Direction",
  directions: { growth: "Growth", decay: "Decay" },
  solveFor: "Solve for",
  unknowns: {
    final: "Final amount (A)",
    initial: "Initial amount (P)",
    rate: "Rate (r)",
    time: "Time (t)",
  },
  initialLabel: "Initial amount (P)",
  finalLabel: "Final amount (A)",
  rateGrowth: "Growth rate (% per {unit})",
  rateDecay: "Decay rate (% per {unit})",
  timeLabel: "Time ({unit})",
  unitLabel: "Time unit",
  unitHint: "Only used for labels, for example years, days or hours.",
  defaultUnit: "periods",
  results: "Results",
  value: "Result",
  factor: "Factor per {unit}",
  discreteRate: "As a discrete rate",
  continuousRate: "As a continuous rate",
  doublingTime: "Doubling time",
  halfLife: "Half-life",
  totalChange: "Total change",
  inUnit: "{value} {unit}",
  perUnit: "{value} per {unit}",
  copy: "Copy result",
  copied: "Copied!",
  workingHeading: "The working",
  stepFormula: "Rearrange the formula for the unknown.",
  stepSubstitute: "Substitute the numbers.",
  stepFactor: "The growth factor for one period is {factor}.",
  formulas: {
    discrete: {
      final: "A = P × (1 + r)^t",
      initial: "P = A / (1 + r)^t",
      rate: "r = (A / P)^(1/t) − 1",
      time: "t = ln(A / P) / ln(1 + r)",
    },
    continuous: {
      final: "A = P × e^(rt)",
      initial: "P = A / e^(rt)",
      rate: "r = ln(A / P) / t",
      time: "t = ln(A / P) / r",
    },
  },
  rulesHeading: "Rule of 72, 70 and 69.3",
  rulesIntro:
    "The rules estimate the doubling or halving time as a constant divided by the rate in percent. Each is an approximation; the exact time is ln 2 divided by ln of the factor.",
  method: "Method",
  rules: {
    exact: "Exact",
    "72": "Rule of 72: 72 / rate",
    "70": "Rule of 70: 70 / rate",
    "69.3": "Rule of 69.3: 69.3 / rate",
  },
  timeIn: "Time ({unit})",
  difference: "Difference from exact",
  scheduleHeading: "Table of values",
  amount: "Amount",
  change: "Change from start",
  graphHeading: "Graph",
  graphTitle:
    "The exponential curve from the start to the end, with key points marked",
  curveLabel: "A(t)",
  point: "({x}, {y})",
  downloadPng: "Download graph (PNG)",
  noChange: "With a rate of 0 nothing changes, so there is no doubling time.",
  enter: "Enter the known values to see the result.",
  errors: {
    missing: "Enter a number in every field that is shown.",
    "initial-positive": "The initial amount must be greater than 0.",
    "final-positive": "The final amount must be greater than 0.",
    "rate-negative":
      "Enter the size of the rate as a positive percentage and choose Growth or Decay.",
    "decay-too-large":
      "A decay rate per period must be below 100% for the discrete model.",
    "rate-zero":
      "With a rate of 0 the amount never changes, so time cannot be solved.",
    "time-zero": "Time cannot be 0 when solving for the rate.",
    "time-negative": "Time cannot be negative.",
    overflow:
      "The result is too large or too small to show. Try smaller numbers.",
  },
} as const;

type Strings = typeof EXPONENTIAL_GROWTH_CALCULATOR_STRINGS;

const UNKNOWN_ORDER: GrowthUnknown[] = ["final", "initial", "rate", "time"];

function Results({
  input,
  unit,
  strings,
}: {
  input: GrowthInput;
  unit: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const outcome = solveGrowth(input);
  if (!outcome.ok) {
    return <ErrorNotice>{strings.errors[outcome.error]}</ErrorNotice>;
  }
  const { result } = outcome;
  const headline: Record<GrowthUnknown, string> = {
    final: amountText(fmt, result.final),
    initial: amountText(fmt, result.initial),
    rate: percentText(fmt, result.rate),
    time: t(strings.inUnit, { value: amountText(fmt, result.time), unit }),
  };
  const characteristic = result.characteristic;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.unknowns[input.solve]}
          value={headline[input.solve]}
        />
        <StatCard
          label={t(strings.factor, { unit })}
          value={fmt(result.factor, { maximumFractionDigits: 6 })}
        />
        <StatCard
          label={strings.totalChange}
          value={`${fmt(result.totalChangePercent, { maximumFractionDigits: 2, signDisplay: "exceptZero" })}%`}
        />
        <StatCard
          label={strings.discreteRate}
          value={t(strings.perUnit, {
            value: percentText(fmt, result.discreteRate),
            unit,
          })}
        />
        <StatCard
          label={strings.continuousRate}
          value={t(strings.perUnit, {
            value: percentText(fmt, result.continuousRate),
            unit,
          })}
        />
        {characteristic ? (
          <StatCard
            label={
              characteristic.kind === "doubling"
                ? strings.doublingTime
                : strings.halfLife
            }
            value={t(strings.inUnit, {
              value: fmt(characteristic.time, { maximumFractionDigits: 4 }),
              unit,
            })}
          />
        ) : null}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={headline[input.solve]}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <WorkingSteps input={input} result={result} strings={strings} />
      {result.rules.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.rulesHeading}</h3>
          <p className="text-muted-foreground text-sm">{strings.rulesIntro}</p>
          <RulesTable result={result} strings={strings} unit={unit} />
        </>
      ) : (
        <Notice>{strings.noChange}</Notice>
      )}
      <h3 className={WIDGET_H3}>{strings.scheduleHeading}</h3>
      <ScheduleTable result={result} strings={strings} unit={unit} />
      <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
      <GrowthChart result={result} strings={strings} />
    </>
  );
}

export default function ExponentialGrowthCalculatorWidget() {
  const strings = useToolUi(EXPONENTIAL_GROWTH_CALCULATOR_STRINGS);
  const [model, setModel] = useState<GrowthModel>("discrete");
  const [direction, setDirection] = useState<GrowthDirection>("growth");
  const [solve, setSolve] = useState<GrowthUnknown>("final");
  const [initial, setInitial] = useState("1000");
  const [final, setFinal] = useState("2000");
  const [rate, setRate] = useState("5");
  const [time, setTime] = useState("10");
  const [unit, setUnit] = useState("");
  const unitText = unit.trim() === "" ? strings.defaultUnit : unit.trim();
  const input: GrowthInput = {
    model,
    direction,
    solve,
    initial: parseNumberInput(initial),
    final: parseNumberInput(final),
    ratePercent: parseNumberInput(rate),
    time: parseNumberInput(time),
  };
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.model}
          name="egc-model"
          onChange={setModel}
          options={(["discrete", "continuous"] as const).map((value) => ({
            value,
            label: strings.models[value],
          }))}
          value={model}
        />
        <PillGroup
          label={strings.direction}
          name="egc-direction"
          onChange={setDirection}
          options={(["growth", "decay"] as const).map((value) => ({
            value,
            label: strings.directions[value],
          }))}
          value={direction}
        />
      </div>
      <SelectField
        id="egc-solve"
        label={strings.solveFor}
        onChange={setSolve}
        options={UNKNOWN_ORDER.map((value) => ({
          value,
          label: strings.unknowns[value],
        }))}
        value={solve}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {solve === "initial" ? null : (
          <TextField
            id="egc-initial"
            label={strings.initialLabel}
            mono
            numeric
            onChange={setInitial}
            value={initial}
          />
        )}
        {solve === "final" ? null : (
          <TextField
            id="egc-final"
            label={strings.finalLabel}
            mono
            numeric
            onChange={setFinal}
            value={final}
          />
        )}
        {solve === "rate" ? null : (
          <TextField
            id="egc-rate"
            label={t(
              direction === "growth" ? strings.rateGrowth : strings.rateDecay,
              {
                unit: unitText,
              }
            )}
            mono
            numeric
            onChange={setRate}
            value={rate}
          />
        )}
        {solve === "time" ? null : (
          <TextField
            id="egc-time"
            label={t(strings.timeLabel, { unit: unitText })}
            mono
            numeric
            onChange={setTime}
            value={time}
          />
        )}
        <TextField
          hint={strings.unitHint}
          id="egc-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          placeholder={strings.defaultUnit}
          value={unit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Results input={input} strings={strings} unit={unitText} />
      </section>
    </>
  );
}
